import axios from 'axios';

// Production: https://api.eezybooking.com (set REACT_APP_API_URL at build time for deploy)
// Development: localhost only when NODE_ENV is 'development'
const isProductionBuild = process.env.NODE_ENV === 'production';
const isProductionDomain = typeof window !== 'undefined' && 
  (window.location.hostname === 'eezybooking.com' || window.location.hostname === 'www.eezybooking.com');

const PRODUCTION_API = 'https://api.eezybooking.com/api';
let FINAL_API_BASE_URL = process.env.REACT_APP_API_URL || (isProductionBuild || isProductionDomain ? PRODUCTION_API : 'https://api.eezybooking.com/api');
// Runtime safety: if on production domain, never use localhost
if (typeof window !== 'undefined' && (window.location.hostname === 'eezybooking.com' || window.location.hostname === 'www.eezybooking.com') && FINAL_API_BASE_URL.indexOf('localhost') !== -1) {
  FINAL_API_BASE_URL = PRODUCTION_API;
}

// Log to verify (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('🔧 API Base URL:', FINAL_API_BASE_URL);
}

// Create axios instance
export const api = axios.create({
  baseURL: FINAL_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Runtime safety check: If on production domain, force production API
    const isProdDomain = typeof window !== 'undefined' && 
      (window.location.hostname === 'eezybooking.com' || 
       window.location.hostname === 'www.eezybooking.com');
    if (isProdDomain && config.baseURL && config.baseURL.includes('localhost')) {
      config.baseURL = 'https://api.eezybooking.com/api';
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Bust caches for GET requests to ensure realtime data
    if ((config.method || '').toLowerCase() === 'get') {
      const existingParams = config.params || {};
      config.params = { ...existingParams, _ts: Date.now() };
    }
    // Remove cache headers on localhost to avoid CORS issues (only in development)
    if (!isProductionBuild && !isProdDomain && FINAL_API_BASE_URL.includes('localhost')) {
      delete config.headers['Cache-Control'];
      delete config.headers['cache-control'];
      delete config.headers.Pragma;
      delete config.headers.pragma;
    }
    // For FormData, remove Content-Type header completely
    // Axios will automatically set it to multipart/form-data with boundary
    // IMPORTANT: Keep Authorization header when using FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      // Ensure Authorization header is preserved
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and session management
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Clear token and redirect to login for auth/admin endpoints
      if (url.includes('/auth/') || url.includes('/admin/')) {
        localStorage.removeItem('token');
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  phoneLogin: (data) => api.post('/auth/phone-login', data),
  verifyOTP: (data) => api.post('/auth/verify-otp', data),
  googleLogin: (data) => api.post('/auth/google-login', data),
  getMe: () => api.get('/auth/me'),
};

export const courtsAPI = {
  getAll: (params) => api.get('/courts', { params }),
  getById: (id) => api.get(`/courts/${id}`),
  addFavorite: (id) => api.post(`/courts/${id}/favorite`),
  removeFavorite: (id) => api.delete(`/courts/${id}/favorite`),
  getBookingStatus: (courtId, date) => api.get(`/courts/${courtId}/booking-status`, { params: { date } }),
  getTimeSlots: (courtId, date) => api.get(`/time-slots/courts/${courtId}/availability`, { params: { date } }),
};

export const tournamentsAPI = {
  getAll: (params = {}) => api.get('/tournaments', { params }),
  getById: (id) => api.get(`/tournaments/${id}`),
  apply: (id, data) => api.post(`/tournaments/${id}/apply`, data),
  checkApplication: (id) => api.get(`/tournaments/${id}/check-application`),
  getMyApplications: () => api.get('/tournaments/applications/my'),
  getAdminApplications: (params) => api.get('/tournaments/admin/applications', { params }),
  updateApplicationStatus: (id, status) => api.patch(`/tournaments/admin/applications/${id}/status`, { status }),
};

export const venuesAPI = {
  getAll: (params = {}) => api.get('/venues', { params }),
  getById: (id, params = {}) => api.get(`/venues/${id}`, { params }),
  getFilters: () => api.get('/venues/filters/options'),
  create: (data) => api.post('/venues', data),
  update: (id, data) => api.put(`/venues/${id}`, data),
  delete: (id) => api.delete(`/venues/${id}`),
  addFavorite: (id) => api.post(`/venues/${id}/favorite`),
  removeFavorite: (id) => api.delete(`/venues/${id}/favorite`),
};

export const rewardsOffersAPI = {
  getPublic: () => api.get('/admin/rewards-offers/public'),
};


export const bookingsAPI = {
  getAll: (params) => api.get('/bookings', { params }),
  getById: (id) => api.get(`/bookings/${id}`),
  create: (data) => api.post('/time-slots/bookings', data),
  updateStatus: (id, status) => api.patch(`/bookings/${id}/status`, { status }),
  cancel: (id) => api.patch(`/bookings/${id}/cancel`),
  delete: (id) => api.delete(`/bookings/${id}`),
};

export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.put('/users/change-password', data),
  getFavorites: (params) => api.get('/users/favorites', { params }),
  getStats: () => api.get('/users/stats'),
  deleteAccount: (data) => api.delete('/users/account', { data }),
};

export const searchAPI = {
  searchCourts: (params) => api.get('/search/courts', { params }),
  getSuggestions: (params) => api.get('/search/suggestions', { params }),
  getPopular: () => api.get('/search/popular'),
  getFilters: () => api.get('/search/filters'),
};

export const timeSlotsAPI = {
  getAvailability: (courtId, date) => api.get(`/time-slots/courts/${courtId}/availability`, { params: { date } }),
  getAdminAvailability: (courtId, date) => api.get(`/time-slots/admin/courts/${courtId}/availability`, { params: { date } }),
  createBooking: (data) => api.post('/time-slots/bookings', data),
};

export const adminAPI = {
  getDashboard: (params) => api.get('/admin/dashboard', { params }),
  getBookings: (params) => api.get('/admin/bookings', { params }),
  updateBookingStatus: (id, status) => api.patch(`/admin/bookings/${id}/status`, { status }),
  getVenues: (params) => api.get('/admin/venues', { params }),
  createVenue: (data) => api.post('/admin/venues', data),
  updateVenue: (id, data) => api.put(`/admin/venues/${id}`, data),
  deleteVenue: (id) => api.delete(`/admin/venues/${id}`),
  getVenueAnalytics: (id) => api.get(`/admin/venues/${id}/analytics`),
  
  // Court Management APIs
  getCourts: (params) => api.get('/admin/courts', { params }),
  createCourt: (data) => api.post('/admin/courts', data),
  updateCourt: (id, data) => api.put(`/admin/courts/${id}`, data),
  deleteCourt: (id) => api.delete(`/admin/courts/${id}`),
  
  // Manager Management APIs
  getManagers: () => api.get('/admin/managers'),
  createManager: (data) => api.post('/admin/managers', data),
  updateManager: (id, data) => api.put(`/admin/managers/${id}`, data),
  deleteManager: (id) => api.delete(`/admin/managers/${id}`),
  
  // Super Admin APIs
  getSuperAdminOverview: (params) => api.get('/admin/super/overview', { params }),
  getAdmins: () => api.get('/admin/admins'),
  getAdminVenues: (adminId) => api.get(`/admin/admins/${adminId}/venues`),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateAdmin: (id, data) => api.put(`/admin/admins/${id}`, data),
  
  // Cities and Locations Management APIs
  getCities: () => api.get('/admin/cities'),
  createCity: (data) => api.post('/admin/cities', data),
  updateCity: (id, data) => api.put(`/admin/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/admin/cities/${id}`),
  getLocations: (params) => api.get('/admin/locations', { params }),
  createLocation: (data) => api.post('/admin/locations', data),
  updateLocation: (id, data) => api.put(`/admin/locations/${id}`, data),
  deleteLocation: (id) => api.delete(`/admin/locations/${id}`),
  getCitiesLocations: () => api.get('/admin/cities-locations'),
  // Rewards and Offers Management APIs
  getRewardsOffers: () => api.get('/admin/rewards-offers'),
  createRewardOffer: (formData) => {
    // Don't set Content-Type header - axios will set it automatically with boundary for FormData
    return api.post('/admin/rewards-offers', formData);
  },
  updateRewardOffer: (id, formData) => {
    // Don't set Content-Type header - axios will set it automatically with boundary for FormData
    return api.put(`/admin/rewards-offers/${id}`, formData);
  },
  deleteRewardOffer: (id) => api.delete(`/admin/rewards-offers/${id}`),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),
  getAllVenues: () => api.get('/admin/super/venues'),
  getAllCourts: () => api.get('/admin/super/courts'),
  getAllBookings: (params) => api.get('/admin/super/bookings', { params }),
  // Venue-wise overview APIs
  getVenueOverview: (params) => api.get('/admin/super/venue-overview', { params }),
  getVenueUsers: (venueId, params) => api.get(`/admin/super/venue/${venueId}/users`, { params }),
  getVenueBookings: (venueId, params) => api.get(`/admin/super/venue/${venueId}/bookings`, { params }),
  getAllUsers: (params) => api.get('/admin/super/users', { params }),
  resetAdminPassword: (adminId, newPassword) => api.post(`/admin/admins/${adminId}/reset-password`, { new_password: newPassword }),
  
  // Super Admin CRUD for Venues
  updateVenueSuper: (id, data) => {
    // Don't set Content-Type header - axios will set it automatically with boundary for FormData
    return api.put(`/admin/super/venues/${id}`, data);
  },
  deleteVenueSuper: (id) => api.delete(`/admin/super/venues/${id}`),
  
  // Super Admin CRUD for Courts
  updateCourtSuper: (id, data) => api.put(`/admin/super/courts/${id}`, data),
  deleteCourtSuper: (id) => api.delete(`/admin/super/courts/${id}`),
  
  // Tournament Management APIs
  getTournaments: (params) => api.get('/tournaments/admin/all', { params }),
  createTournament: (formData) => {
    // Don't set Content-Type - axios will set it with boundary automatically
    return api.post('/tournaments/admin', formData);
  },
  updateTournament: (id, formData) => {
    // Don't set Content-Type - axios will set it with boundary automatically
    return api.put(`/tournaments/admin/${id}`, formData);
  },
  deleteTournament: (id) => api.delete(`/tournaments/admin/${id}`),
  approveTournament: (id, isApproved) => api.patch(`/tournaments/admin/${id}/approval`, { is_approved: isApproved }),
  
  // Invoice and Payment Management APIs
  createInvoice: (bookingId, data) => api.post(`/admin/bookings/${bookingId}/invoice`, data),
  createGroupInvoice: (groupId, data) => api.post(`/admin/booking-groups/${groupId}/invoice`, data),
  getInvoice: (bookingId) => api.get(`/admin/bookings/${bookingId}/invoice`),
  getInvoiceById: (invoiceId) => api.get(`/admin/invoices/${invoiceId}`),
  addPayment: (invoiceId, data) => api.post(`/admin/invoices/${invoiceId}/payments`, data),
  getPayments: (invoiceId) => api.get(`/admin/invoices/${invoiceId}/payments`),
  getInvoices: (params) => api.get('/admin/invoices', { params }),
  cancelInvoice: (invoiceId) => api.patch(`/admin/invoices/${invoiceId}/cancel`),
  deleteInvoice: (invoiceId) => api.delete(`/admin/invoices/${invoiceId}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Marketplace API
export const marketplaceAPI = {
  getAllProducts: (params) => api.get('/marketplace', { params }),
  getProduct: (id) => api.get(`/marketplace/${id}`),
  getMyProducts: () => api.get('/marketplace/user/my-products'),
  getBrands: () => api.get('/marketplace/brands'),
  submitProduct: (data) => api.post('/marketplace/submit', data),
  updateProduct: (id, data) => api.put(`/marketplace/${id}`, data),
  deleteProduct: (id) => api.delete(`/marketplace/${id}`),
  
  // Admin APIs (Super Admin only)
  getAllProductsAdmin: (params) => api.get('/marketplace/admin/all', { params }),
  approveProduct: (id) => api.put(`/marketplace/admin/approve/${id}`),
  rejectProduct: (id) => api.delete(`/marketplace/admin/reject/${id}`),
};

// Reviews API
export const reviewsAPI = {
  getByVenue: (venueId) => api.get(`/reviews/venue/${venueId}`),
  create: (data) => api.post('/reviews', data),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  delete: (id) => api.delete(`/reviews/${id}`),
  approve: (id) => api.put(`/reviews/${id}/approve`),
  reject: (id) => api.put(`/reviews/${id}/reject`),
  getAll: (params) => api.get('/reviews/admin/all', { params })
};

// Upload API
export const uploadAPI = {
  uploadProductImage: (formData) => api.post('/upload/product-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  uploadVenueImage: (formData) => api.post('/upload/venue-image', formData),
  uploadAvatar: (formData) => api.post('/upload/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  uploadCoachImage: (formData) => api.post('/upload/coach-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
};

export const coachingAPI = {
  getCoaches: (params) => api.get('/coaching/coaches', { params }),
  getCoach: (id) => api.get(`/coaching/coaches/${id}`),
  getMyCoach: () => api.get('/coaching/my-coach'),
  createCoachProfile: (data) => api.post('/coaching/coaches', data),
  updateCoachProfile: (data) => api.put('/coaching/coaches', data),
  bookSession: (data) => api.post('/coaching/sessions', data),
  getSessions: (params) => api.get('/coaching/sessions', { params }),
  updateSessionStatus: (id, status) => api.patch(`/coaching/sessions/${id}/status`, { status }),
  addReview: (sessionId, data) => api.post(`/coaching/sessions/${sessionId}/review`, data),
  
  // Admin functions
  getPendingCoaches: (params) => api.get('/coaching/admin/pending-coaches', { params }),
  getAllCoaches: (params) => api.get('/coaching/admin/coaches', { params }),
  approveCoach: (coachId, data) => api.patch(`/coaching/admin/coaches/${coachId}/approval`, data),
  rejectCoach: (coachId, data) => api.patch(`/coaching/admin/coaches/${coachId}/approval`, data),
  updateCoach: (coachId, data) => api.put(`/coaching/admin/coaches/${coachId}`, data),
  deleteCoach: (coachId) => api.delete(`/coaching/admin/coaches/${coachId}`),
};

export default api;
